import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MemberCardDisplay from "@/components/MemberCardDisplay";
import AddToWallet from "@/components/AddToWallet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  ChevronLeft, 
  User, 
  QrCode, 
  CreditCard, 
  Phone, 
  Mail, 
  MapPin, 
  Settings, 
  LogOut, 
  ChevronRight,
  Edit2,
  Crown,
  Gift,
  Star,
  Shield,
  Bell,
  Users,
  Wallet,
  Plus,
  Check
} from "lucide-react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { BRAND } from "@/styles/brand";

// ═══════════════════════════════════════════════════════════
// COLORS - Unified Brand Palette
// ═══════════════════════════════════════════════════════════
const COLORS = {
  background: "#F5E6E0",      // Warm beige/pink - BRAND
  green: "#2D5A3D",           // Forest Green
  greenLight: "#4A7C59",
  orange: "#E07A3D",          // Rust Orange
  orangeLight: "#F08A4D",
  text: "#1A1A1A",
  textMuted: "#6B5B4F",
  white: "#FFFFFF",
  cream: "#FFF9F0",
  matcha: "#4A7C59",
  red: BRAND.colors.error,
};

// ═══════════════════════════════════════════════════════════
// MEMBER PROFILE PAGE
// ═══════════════════════════════════════════════════════════
const CARD_IMAGE_MAP = {
  pikachu: "/membercards/2.png",
  erdmaennchen: "/membercards/3.png",
  palestine: "/membercards/4.png",
  hund: "/membercards/5.png",
  katze: "/membercards/6.png",
};

export default function MemberProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, logout, setShowAuthModal } = useAuth();
  const [showQRModal, setShowQRModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [membershipDetails, setMembershipDetails] = useState(null);
  const [loadingMembership, setLoadingMembership] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Check for successful top-up/giftcard
  useEffect(() => {
    const topupStatus = searchParams.get("topup");
    const giftcardStatus = searchParams.get("giftcard");
    const sessionId = searchParams.get("session_id");
    
    if (topupStatus === "success" && sessionId) {
      checkTopUpStatus(sessionId);
    } else if (giftcardStatus === "success" && sessionId) {
      checkGiftCardStatus(sessionId);
    }
  }, [searchParams]);

  // Load balance data
  useEffect(() => {
    if (user && token) {
      loadBalanceData();
      loadMembershipDetails();
    }
  }, [user, token]);

  const loadMembershipDetails = async () => {
    setLoadingMembership(true);
    try {
      const res = await axios.get(`${API}/membership/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembershipDetails(res.data);
    } catch (err) {
      // User ohne Abo ist ein legitimer Fall - kein Toast
    } finally {
      setLoadingMembership(false);
    }
  };

  const handleCancelMembership = async () => {
    if (!window.confirm("Möchtest du dein Abo wirklich kündigen? Es läuft noch 3 Monate weiter.")) {
      return;
    }
    setCancelling(true);
    try {
      const res = await axios.post(
        `${API}/membership/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data.message || "Kündigung erfasst");
      await loadMembershipDetails();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kündigung fehlgeschlagen");
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "–";
    try {
      return new Date(iso).toLocaleDateString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
      });
    } catch { return iso; }
  };

  const loadBalanceData = async () => {
    setLoadingBalance(true);
    try {
      const res = await axios.get(`${API}/balance/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalanceData(res.data);
    } catch (err) {
      console.error("Failed to load balance:", err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const checkTopUpStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/balance/topup/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === "completed") {
        toast.success(`${res.data.amount}€ Guthaben aufgeladen!${res.data.bonus ? ` + ${res.data.bonus}` : ""}`);
        loadBalanceData();
      }
    } catch (err) {
      console.error("TopUp status check failed:", err);
    }
  };

  const checkGiftCardStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/giftcard/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.status === "active") {
        toast.success(`Geschenkgutschein erstellt! Code: ${res.data.code}`);
        loadBalanceData();
      }
    } catch (err) {
      console.error("GiftCard status check failed:", err);
    }
  };

  // If not logged in, show login/register screen
  if (!user) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: COLORS.background }}>
        {/* Header */}
        <header 
          className="px-4 pt-6 pb-4 flex items-center gap-3"
          style={{ backgroundColor: COLORS.white }}
        >
          <motion.button 
            whileTap={{ scale: 0.9 }} 
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl"
            style={{ backgroundColor: COLORS.cream }}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: COLORS.text }} />
          </motion.button>
          <h1 className="text-lg font-bold" style={{ color: COLORS.text }}>
            Mein Profil
          </h1>
        </header>

        {/* Login/Register Content */}
        <div className="flex flex-col items-center justify-center px-6 py-12">
          {/* Illustration */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="w-32 h-32 rounded-full flex items-center justify-center mb-8"
            style={{ backgroundColor: COLORS.green + "15" }}
          >
            <User className="w-16 h-16" style={{ color: COLORS.green }} />
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-2xl font-bold text-center mb-3"
            style={{ color: COLORS.text }}
          >
            Willkommen bei Franky's!
          </motion.h2>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-8 max-w-xs"
            style={{ color: COLORS.textMuted }}
          >
            Melde dich an oder erstelle ein Konto um Stempel zu sammeln, Coupons zu erhalten und exklusive Vorteile zu genießen.
          </motion.p>

          {/* Buttons */}
          <div className="w-full max-w-xs space-y-4">
            {/* Login Button */}
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setShowAuthModal(true);
                setTimeout(() => {
                  document.dispatchEvent(new CustomEvent('set-auth-mode', { detail: 'login' }));
                }, 100);
              }}
              className="w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: COLORS.green,
                color: COLORS.white
              }}
              data-testid="profile-login-btn"
            >
              <LogOut className="w-5 h-5 rotate-180" />
              Anmelden
            </motion.button>

            {/* Register Button */}
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setShowAuthModal(true);
                setTimeout(() => {
                  document.dispatchEvent(new CustomEvent('set-auth-mode', { detail: 'register' }));
                }, 100);
              }}
              className="w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 border-2"
              style={{ 
                backgroundColor: COLORS.white,
                borderColor: COLORS.orange,
                color: COLORS.orange
              }}
              data-testid="profile-register-btn"
            >
              <Plus className="w-5 h-5" />
              Registrieren
            </motion.button>
          </div>

          {/* Benefits Preview */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-10 w-full max-w-xs"
          >
            <p 
              className="text-xs font-bold uppercase tracking-wider mb-4 text-center"
              style={{ color: COLORS.textMuted }}
            >
              Deine Vorteile
            </p>
            <div className="space-y-3">
              {[
                { icon: "🎁", text: "Willkommens-Coupon" },
                { icon: "☕", text: "Stempelkarte - 10 Stempel = Gratis Getränk" },
                { icon: "💰", text: "Exklusive Mitglieder-Rabatte" },
              ].map((benefit, i) => (
                <div 
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ backgroundColor: COLORS.white }}
                >
                  <span className="text-xl">{benefit.icon}</span>
                  <span className="text-sm font-medium" style={{ color: COLORS.text }}>
                    {benefit.text}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Generate unique QR code value for this user
  const qrValue = user?.member_code || "";

  // Membership data from backend
  const membership = user.membership || null;
  const memberships = user.memberships || (membership ? [membership] : []);
  const activeMemberships = memberships.filter(m => m.active);
  const hasActiveMembership = activeMemberships.length > 0;
  
  // Determine plan type from plan_id or type
  const membershipPlanId = membership?.plan_id || membership?.type;

  const handleLogout = () => {
    logout();
    navigate("/landing");
    toast.success("Erfolgreich abgemeldet");
  };

  // Profile menu items
  const menuSections = [
    {
      title: "Guthaben & Geschenke",
      items: [
        {
          icon: Wallet,
          label: "Mein Guthaben",
          subtitle: loadingBalance ? "Lädt..." : `${balanceData?.balance?.toFixed(2) || "0.00"}€ verfügbar`,
          color: COLORS.green,
          action: () => setShowTopUpModal(true),
          badge: balanceData?.pending_bonuses?.length > 0 ? `${balanceData.pending_bonuses.length} Bonus` : null
        },
        {
          icon: Gift,
          label: "Geschenkgutschein kaufen",
          subtitle: "Verschenke Freude",
          color: COLORS.orange,
          action: () => setShowGiftCardModal(true),
          badge: null
        }
      ]
    },
    {
      title: "Stempel & Punkte",
      items: [
        {
          icon: QrCode,
          label: "Mein QR-Code",
          subtitle: "Zum Scannen vorzeigen",
          color: COLORS.green,
          action: () => setShowQRModal(true),
          badge: null
        },
        {
          icon: Star,
          label: "Meine Punkte",
          subtitle: `${user.points || 0} Punkte gesammelt`,
          color: COLORS.orange,
          action: () => navigate("/home?tab=stamps"),
          badge: user.points > 0 ? user.points : null
        },
        {
          icon: Gift,
          label: "Meine Coupons",
          subtitle: "Eingelöste & verfügbare Coupons",
          color: COLORS.matcha,
          action: () => navigate("/home?tab=coupons"),
          badge: null
        },
        {
          icon: Users,
          label: "Freunde einladen",
          subtitle: "Teile & verdiene 3 Stempel",
          color: COLORS.orange,
          action: () => navigate("/referral"),
          badge: null
        }
      ]
    },
    {
      title: "Membership",
      items: hasActiveMembership ? [
        {
          icon: Crown,
          label: membershipDetails?.plan_name || (membershipPlanId === "matcha" ? "Franky's Matcha Club" : "Franky's Coffee Pass"),
          subtitle: membershipDetails?.paused
            ? "⚠️ Pausiert – Zahlung fehlgeschlagen"
            : membershipDetails?.cancel_at
              ? `Läuft noch bis ${formatDate(membershipDetails.cancel_at)}`
              : membershipDetails?.remaining_drinks !== null && membershipDetails?.remaining_drinks !== undefined
                ? `${membershipDetails.remaining_drinks}/${membershipDetails.period === "week" ? 1 : 1} ${membershipDetails.period === "week" ? "diese Woche" : "heute"} übrig`
                : "Aktiv",
          color: membershipDetails?.paused ? COLORS.red : (membershipPlanId === "matcha" ? COLORS.matcha : COLORS.orange),
          action: () => setShowMembershipModal(true),
          badge: membershipDetails?.paused ? "Pausiert" : (membershipDetails?.cancel_at ? "Gekündigt" : "Aktiv"),
        },
        {
          icon: CreditCard,
          label: "Abo verwalten",
          subtitle: membershipDetails?.next_payment_at
            ? `Nächste Zahlung: ${formatDate(membershipDetails.next_payment_at)}`
            : "Zahlungen & Kündigung",
          color: COLORS.text,
          action: () => setShowMembershipModal(true),
          badge: null,
        },
      ] : [
        {
          icon: Crown,
          label: "Mitglied werden",
          subtitle: "1 Matcha pro Woche + 10 % auf alles",
          color: COLORS.green,
          action: () => navigate("/membership"),
          badge: "Neu",
        },
      ],
    },
    {
      title: "Persönliche Daten",
      items: [
        {
          icon: User,
          label: "Name",
          subtitle: user.name || "Nicht angegeben",
          color: COLORS.text,
          action: () => toast.info("Bearbeitung kommt bald"),
          editable: true
        },
        {
          icon: Mail,
          label: "E-Mail",
          subtitle: user.email,
          color: COLORS.text,
          action: () => toast.info("E-Mail ändern kommt bald"),
          editable: true
        },
        {
          icon: Phone,
          label: "Telefon",
          subtitle: user.phone || "Nicht angegeben",
          color: COLORS.text,
          action: () => toast.info("Bearbeitung kommt bald"),
          editable: true
        },
        {
          icon: MapPin,
          label: "Standort",
          subtitle: user.location === "mannheim" ? "Mannheim" : "Aachen",
          color: COLORS.text,
          action: () => {
            localStorage.removeItem("frankysfarm_location");
            navigate("/");
            toast.success("Standort wird zurückgesetzt");
          },
          editable: true
        }
      ]
    },
    {
      title: "Einstellungen",
      items: [
        {
          icon: Bell,
          label: "Benachrichtigungen",
          subtitle: "Push & E-Mail Einstellungen",
          color: COLORS.text,
          action: () => toast.info("Einstellungen kommen bald"),
          badge: null
        },
        {
          icon: Shield,
          label: "Datenschutz",
          subtitle: "Deine Daten verwalten",
          color: COLORS.text,
          action: () => toast.info("Datenschutz kommt bald"),
          badge: null
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: COLORS.background }}>
      {/* Header */}
      <header 
        className="px-4 pt-6 pb-4 flex items-center gap-3"
        style={{ backgroundColor: COLORS.white }}
      >
        <motion.button 
          whileTap={{ scale: 0.9 }} 
          onClick={() => navigate(-1)}
          className="p-2 rounded-xl"
          style={{ backgroundColor: COLORS.cream }}
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: COLORS.text }} />
        </motion.button>
        <h1 className="text-lg font-bold" style={{ color: COLORS.text }}>
          Mein Profil
        </h1>
      </header>

      {/* Profile Card */}
      <section className="px-5 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-3xl relative overflow-hidden"
          style={{ backgroundColor: COLORS.green }}
        >
          {/* Background decoration */}
          <div 
            className="absolute -top-10 -right-10 w-32 h-32 rounded-full"
            style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
          />
          <div 
            className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full"
            style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
          />
          
          <div className="relative flex items-center gap-4">
            {/* Avatar */}
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold"
              style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
            >
              <span className="text-white">
                {user.name ? user.name.charAt(0).toUpperCase() : "👤"}
              </span>
            </div>
            
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-1">
                {user.name || "Franky's Fan"}
              </h2>
              <p className="text-sm text-white/70">
                {user.email}
              </p>
              {hasActiveMembership && (
                <span 
                  className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                >
                  <Crown className="w-3 h-3 text-yellow-300" />
                  <span className="text-white">
                    {activeMemberships.map(m => m.name || m.plan_id).join(" + ") || "Member"}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* QR Code is now on the MemberCardDisplay */}
        </motion.div>
      </section>

      {/* Member Card Display */}
      {/* Member Card - always visible */}
      <section className="px-5 mt-4">
        <MemberCardDisplay
          cardId={user?.member_card || "default"}
          cardImage={CARD_IMAGE_MAP[user?.member_card] || "/membercards/2.png"}
          memberCode={user?.member_code || ""}
          userName={user?.name || "Farm Member"}
          qrValue={user?.member_code || ""}
        />
      </section>

      {/* Add to Wallet */}
      <div className="flex justify-center mt-2 px-5">
        <AddToWallet cardId={user?.member_card || "default"} />
      </div>



      {/* Menu Sections */}
      {menuSections.map((section, sectionIndex) => (
        <section key={sectionIndex} className="px-5 py-2">
          <h3 
            className="text-xs font-bold uppercase tracking-wide mb-3 px-1"
            style={{ color: COLORS.textMuted }}
          >
            {section.title}
          </h3>
          <div 
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: COLORS.white }}
          >
            {section.items.map((item, itemIndex) => {
              const Icon = item.icon;
              return (
                <motion.button
                  key={itemIndex}
                  whileTap={{ scale: 0.98 }}
                  onClick={item.action}
                  className="w-full p-4 flex items-center gap-3 text-left"
                  style={{ 
                    borderBottom: itemIndex < section.items.length - 1 
                      ? `1px solid ${COLORS.cream}` 
                      : 'none'
                  }}
                  data-testid={`menu-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium" style={{ color: COLORS.text }}>
                      {item.label}
                    </p>
                    <p className="text-sm truncate" style={{ color: COLORS.textMuted }}>
                      {item.subtitle}
                    </p>
                  </div>
                  {item.badge && (
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-bold text-white"
                      style={{ backgroundColor: item.color }}
                    >
                      {item.badge}
                    </span>
                  )}
                  {item.editable && (
                    <Edit2 className="w-4 h-4" style={{ color: COLORS.textMuted }} />
                  )}
                  <ChevronRight className="w-5 h-5" style={{ color: COLORS.textMuted }} />
                </motion.button>
              );
            })}
          </div>
        </section>
      ))}

      {/* Logout Button */}
      <section className="px-5 py-4">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          className="w-full p-4 rounded-2xl flex items-center justify-center gap-2"
          style={{ backgroundColor: `${COLORS.red}10` }}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" style={{ color: COLORS.red }} />
          <span className="font-medium" style={{ color: COLORS.red }}>
            Abmelden
          </span>
        </motion.button>
      </section>

      {/* App Version */}
      <p className="text-center text-xs py-4" style={{ color: COLORS.textMuted }}>
        Franky's Farm App v1.0
      </p>

      {/* QR Code Modal */}
      <AnimatePresence>
        {showQRModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6"
            onClick={() => setShowQRModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm p-6 rounded-3xl"
              style={{ backgroundColor: COLORS.white }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.text }}>
                  Dein QR-Code
                </h3>
                <p className="text-sm mb-6" style={{ color: COLORS.textMuted }}>
                  Zeige diesen Code dem Mitarbeiter zum Scannen
                </p>

                {/* QR Code */}
                <div 
                  className="p-4 rounded-2xl mx-auto inline-block mb-4"
                  style={{ backgroundColor: COLORS.cream }}
                >
                  <QRCode
                    value={qrValue}
                    size={200}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    viewBox={`0 0 256 256`}
                    fgColor={COLORS.green}
                    bgColor="transparent"
                  />
                </div>

                {/* User Info */}
                <div 
                  className="p-3 rounded-xl mb-4"
                  style={{ backgroundColor: `${COLORS.green}10` }}
                >
                  <p className="font-bold" style={{ color: COLORS.green }}>
                    {user.name || user.email}
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>
                    {user.points || 0} Punkte • {user.location === "mannheim" ? "Mannheim" : "Aachen"}
                  </p>
                </div>

                {/* Info Text */}
                <p className="text-xs mb-4" style={{ color: COLORS.textMuted }}>
                  Nach dem Scan erhältst du automatisch deinen Stempel oder deine Punkte gutgeschrieben.
                </p>

                {/* Close Button */}
                <button
                  onClick={() => setShowQRModal(false)}
                  className="w-full py-3 rounded-xl font-bold text-white"
                  style={{ backgroundColor: COLORS.green }}
                  data-testid="close-qr-modal-btn"
                >
                  Schließen
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* TOP-UP MODAL */}
        {showTopUpModal && (
          <TopUpModal 
            isOpen={showTopUpModal} 
            onClose={() => setShowTopUpModal(false)} 
            token={token}
            balanceData={balanceData}
            onSuccess={loadBalanceData}
          />
        )}

        {/* GIFT CARD MODAL */}
        {showGiftCardModal && (
          <GiftCardModal
            isOpen={showGiftCardModal}
            onClose={() => setShowGiftCardModal(false)}
            token={token}
          />
        )}

        {/* MEMBERSHIP DETAIL MODAL */}
        {showMembershipModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4"
            style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
            onClick={() => setShowMembershipModal(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
              style={{ backgroundColor: COLORS.white }}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-extrabold" style={{ color: COLORS.text }}>
                  Dein Abo
                </h2>
                <button
                  onClick={() => setShowMembershipModal(false)}
                  aria-label="Schließen"
                  className="rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: COLORS.cream, minWidth: 44, minHeight: 44 }}
                >
                  <ChevronLeft className="w-5 h-5 rotate-90" />
                </button>
              </div>

              {loadingMembership && (
                <div className="text-center py-8" style={{ color: COLORS.textMuted }}>Lade Details…</div>
              )}

              {!loadingMembership && membershipDetails?.has_membership && (
                <div className="space-y-4">
                  {membershipDetails.paused && (
                    <div className="p-3 rounded-xl text-sm" style={{ backgroundColor: COLORS.red + "18", color: COLORS.red }}>
                      <b>Abo pausiert:</b> Letzte Zahlung fehlgeschlagen. Bitte Zahlungsmethode in Stripe aktualisieren.
                    </div>
                  )}
                  {membershipDetails.cancel_at && (
                    <div className="p-3 rounded-xl text-sm" style={{ backgroundColor: COLORS.orange + "18", color: COLORS.orange }}>
                      Dein Abo endet am <b>{formatDate(membershipDetails.cancel_at)}</b>. Bis dahin bleiben alle Vorteile aktiv.
                    </div>
                  )}

                  <div
                    className="p-4 rounded-2xl"
                    style={{
                      backgroundColor:
                        membershipDetails.plan_id === "matcha" ? COLORS.matcha + "18" : COLORS.orange + "18",
                    }}
                  >
                    <div className="text-xs uppercase tracking-wider mb-1" style={{ color: COLORS.textMuted }}>
                      Plan
                    </div>
                    <div className="text-lg font-extrabold" style={{ color: COLORS.text }}>
                      {membershipDetails.plan_name}
                    </div>
                    <div className="text-sm mt-1" style={{ color: COLORS.textMuted }}>
                      {membershipDetails.price?.toFixed(2)} € / Monat
                    </div>
                  </div>

                  <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.cream }}>
                      <div className="text-xs" style={{ color: COLORS.textMuted }}>Abo gestartet</div>
                      <div className="font-bold text-sm" style={{ color: COLORS.text }}>
                        {formatDate(membershipDetails.started_at)}
                      </div>
                    </div>
                    <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.cream }}>
                      <div className="text-xs" style={{ color: COLORS.textMuted }}>Nächste Zahlung</div>
                      <div className="font-bold text-sm" style={{ color: COLORS.text }}>
                        {formatDate(membershipDetails.next_payment_at)}
                      </div>
                    </div>
                  </div>

                  {membershipDetails.period === "week" && (
                    <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.green + "12" }}>
                      <div className="text-xs" style={{ color: COLORS.textMuted }}>Diese Woche verfügbar</div>
                      <div className="font-extrabold text-lg" style={{ color: COLORS.green }}>
                        {membershipDetails.remaining_drinks} von 1 Matcha
                      </div>
                      <div className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
                        Einlösbar per QR-Code bis {formatDate(membershipDetails.period_end)}
                      </div>
                    </div>
                  )}
                  {membershipDetails.period === "day" && (
                    <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.orange + "12" }}>
                      <div className="text-xs" style={{ color: COLORS.textMuted }}>Heute verfügbar</div>
                      <div className="font-extrabold text-lg" style={{ color: COLORS.orange }}>
                        {membershipDetails.remaining_drinks} von 1 Kaffee
                      </div>
                    </div>
                  )}

                  {Array.isArray(membershipDetails.features) && membershipDetails.features.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider mb-2" style={{ color: COLORS.textMuted }}>
                        Inklusive
                      </div>
                      <ul className="space-y-1 text-sm" style={{ color: COLORS.text }}>
                        {membershipDetails.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: COLORS.green }} />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="pt-2 space-y-2">
                    <button
                      onClick={() => { setShowMembershipModal(false); setShowQRModal(true); }}
                      className="w-full py-3 rounded-xl font-bold"
                      style={{ backgroundColor: COLORS.green, color: COLORS.white }}
                    >
                      QR-Code zum Einlösen anzeigen
                    </button>

                    {!membershipDetails.cancel_at && (
                      <button
                        onClick={handleCancelMembership}
                        disabled={cancelling}
                        className="w-full py-3 rounded-xl font-bold"
                        style={{ backgroundColor: COLORS.cream, color: COLORS.red }}
                      >
                        {cancelling ? "Kündige…" : "Abo kündigen"}
                      </button>
                    )}
                    <p className="text-xs text-center" style={{ color: COLORS.textMuted }}>
                      Bei Kündigung läuft dein Abo noch 3 Monate weiter. In dieser Zeit kannst du alle Vorteile weiter nutzen.
                    </p>
                  </div>
                </div>
              )}

              {!loadingMembership && !membershipDetails?.has_membership && (
                <div className="text-center py-6">
                  <p style={{ color: COLORS.textMuted }}>Du hast aktuell kein aktives Abo.</p>
                  <button
                    onClick={() => { setShowMembershipModal(false); navigate("/membership"); }}
                    className="mt-4 px-6 py-3 rounded-xl font-bold"
                    style={{ backgroundColor: COLORS.green, color: COLORS.white }}
                  >
                    Jetzt Abo abschließen
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TOP-UP MODAL
// ═══════════════════════════════════════════════════════════
const TopUpModal = ({ isOpen, onClose, token, balanceData, onSuccess }) => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [loading, setLoading] = useState(false);

  const tiers = [
    { amount: 25, bonus: null, bonusText: null },
    { amount: 50, bonus: "free_coffee", bonusText: "Gratis Kaffee" },
    { amount: 100, bonus: "free_ice_matcha", bonusText: "Gratis Ice Matcha Latte" },
    { amount: 200, bonus: "free_breakfast", bonusText: "Gratis Frühstück" },
  ];

  const handleTopUp = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/balance/topup`, 
        { amount: selectedAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Fehler beim Aufladen");
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="w-full max-w-md p-6 rounded-t-3xl sm:rounded-3xl"
        style={{ backgroundColor: COLORS.white }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />
        
        <h3 className="text-xl font-bold mb-2 text-center" style={{ color: COLORS.text }}>
          Guthaben aufladen
        </h3>
        <p className="text-sm mb-4 text-center" style={{ color: COLORS.textMuted }}>
          Aktuelles Guthaben: <span className="font-bold" style={{ color: COLORS.green }}>{balanceData?.balance?.toFixed(2) || "0.00"}€</span>
        </p>

        {/* Pending Bonuses */}
        {balanceData?.pending_bonuses?.length > 0 && (
          <div className="mb-4 p-3 rounded-xl" style={{ backgroundColor: `${COLORS.orange}15` }}>
            <p className="text-xs font-bold mb-1" style={{ color: COLORS.orange }}>
              🎁 Offene Boni:
            </p>
            {balanceData.pending_bonuses.map((b, i) => (
              <p key={i} className="text-sm" style={{ color: COLORS.text }}>• {b.text}</p>
            ))}
          </div>
        )}

        {/* Amount Selection */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {tiers.map((tier) => (
            <motion.button
              key={tier.amount}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedAmount(tier.amount)}
              className="p-4 rounded-2xl text-left relative overflow-hidden"
              style={{ 
                backgroundColor: selectedAmount === tier.amount ? COLORS.green : COLORS.cream,
                border: selectedAmount === tier.amount ? "none" : "2px solid #E5E5E5"
              }}
            >
              <p className="text-lg font-black" style={{ color: selectedAmount === tier.amount ? "#FFF" : COLORS.text }}>
                {tier.amount}€
              </p>
              {tier.bonusText ? (
                <p className="text-xs" style={{ color: selectedAmount === tier.amount ? "rgba(255,255,255,0.8)" : COLORS.orange }}>
                  + {tier.bonusText}
                </p>
              ) : (
                <p className="text-xs" style={{ color: selectedAmount === tier.amount ? "rgba(255,255,255,0.6)" : COLORS.textMuted }}>
                  Ohne Bonus
                </p>
              )}
              {selectedAmount === tier.amount && (
                <div className="absolute top-2 right-2">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </motion.button>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl font-bold"
            style={{ backgroundColor: COLORS.cream, color: COLORS.text }}
          >
            Abbrechen
          </button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleTopUp}
            disabled={loading}
            className="flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2"
            style={{ backgroundColor: COLORS.green }}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Plus className="w-4 h-4" />
                {selectedAmount}€ aufladen
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// GIFT CARD MODAL
// ═══════════════════════════════════════════════════════════
const GiftCardModal = ({ isOpen, onClose, token }) => {
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const amounts = [10, 25, 50, 100];

  const handleCreate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/giftcard/create`, 
        { 
          amount: selectedAmount,
          recipient_name: recipientName || null,
          message: message || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Fehler beim Erstellen des Gutscheins");
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="w-full max-w-md p-6 rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: COLORS.white }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />
        
        <h3 className="text-xl font-bold mb-2 text-center" style={{ color: COLORS.text }}>
          🎁 Geschenkgutschein
        </h3>
        <p className="text-sm mb-4 text-center" style={{ color: COLORS.textMuted }}>
          Verschenke Freude an Familie & Freunde
        </p>

        {/* Amount Selection */}
        <p className="text-xs font-bold mb-2" style={{ color: COLORS.text }}>Betrag wählen</p>
        <div className="flex gap-2 mb-4">
          {amounts.map((amt) => (
            <motion.button
              key={amt}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedAmount(amt)}
              className="flex-1 py-3 rounded-xl font-bold"
              style={{ 
                backgroundColor: selectedAmount === amt ? COLORS.orange : COLORS.cream,
                color: selectedAmount === amt ? "#FFF" : COLORS.text
              }}
            >
              {amt}€
            </motion.button>
          ))}
        </div>

        {/* Recipient Name */}
        <p className="text-xs font-bold mb-2" style={{ color: COLORS.text }}>Für wen? (optional)</p>
        <input
          type="text"
          placeholder="Name des Empfängers"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          className="w-full p-3 rounded-xl mb-4 outline-none"
          style={{ backgroundColor: COLORS.cream, color: COLORS.text }}
        />

        {/* Message */}
        <p className="text-xs font-bold mb-2" style={{ color: COLORS.text }}>Persönliche Nachricht (optional)</p>
        <textarea
          placeholder="z.B. Alles Gute zum Geburtstag!"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          className="w-full p-3 rounded-xl mb-4 outline-none resize-none"
          style={{ backgroundColor: COLORS.cream, color: COLORS.text }}
        />

        {/* Preview Card */}
        <div 
          className="p-4 rounded-2xl mb-4 text-center"
          style={{ 
            background: "linear-gradient(135deg, #7DB95A 0%, #4A7C59 100%)",
          }}
        >
          <p className="text-sm text-white/70">Franky's Farm Gutschein</p>
          <p className="text-3xl font-black text-white my-2">{selectedAmount}€</p>
          {recipientName && <p className="text-sm text-white">Für: {recipientName}</p>}
          {message && <p className="text-xs text-white/80 mt-1 italic">"{message}"</p>}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl font-bold"
            style={{ backgroundColor: COLORS.cream, color: COLORS.text }}
          >
            Abbrechen
          </button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleCreate}
            disabled={loading}
            className="flex-1 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2"
            style={{ backgroundColor: COLORS.orange }}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Gift className="w-4 h-4" />
                Kaufen
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};
